import { FC, useEffect, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './PunchupImagePreview.module.css';

type ImageStatus = 'idle' | 'loading' | 'error' | 'loaded';

export interface PunchupImagePreviewProps {
	/**
	 * URL of the image to display. Omit or pass undefined to show
	 * the static "No preview available" placeholder.
	 */
	src?: string;
	alt?: string;
	/** Max rendered height of the image in px. Defaults to 280. */
	maxHeight?: number;
	/**
	 * Extra CSS class applied to the root element so callers can
	 * add border / border-radius / overflow when needed.
	 */
	className?: string;
	/**
	 * Whether the loading spinner may render. Pass false when an ancestor already
	 * shows its own loading indicator over this preview (e.g. a form's save overlay),
	 * so the user sees one spinner instead of two. Only the spinner is gated — the
	 * idle / loading / error / loaded state machine is unchanged.
	 */
	showSpinner?: boolean;
}

/**
 * PunchupImagePreview
 *
 * Renders a single image with a four-state UX:
 *   idle    — no src: static "No preview available" placeholder
 *   loading — src provided, browser loading: blurred placeholder + spinner overlay
 *             (spinner omitted when showSpinner is false)
 *   error   — src failed to load: static "No preview available" placeholder
 *   loaded  — src loaded OK: image at full width up to maxHeight
 *
 * Does NOT provide its own border or background — the parent container
 * is responsible for those (e.g. .persistedWrapper, .imgContentPreviewWrapper).
 * Add overflow:hidden + border-radius on the parent to clip the image cleanly.
 */
export const PunchupImagePreview: FC<PunchupImagePreviewProps> = ({
	src,
	alt,
	maxHeight = 280,
	className,
	showSpinner = true,
}) => {
	const { t } = useTranslation();
	const [status, setStatus] = useState<ImageStatus>(src ? 'loading' : 'idle');

	// Reset the load-state machine when src changes: a new image must re-enter 'loading'
	// (or 'idle' when cleared); the hidden img's onLoad/onError then advance it from there.
	useEffect(() => {
		setStatus(src ? 'loading' : 'idle');
	}, [src]);

	const rootClass = [styles.root, className].filter(Boolean).join(' ');
	const showPlaceholder = status !== 'loaded';
	const isLoading = status === 'loading';

	return (
		<div className={rootClass}>
			{/* Hidden img drives the load/error state machine without flickering */}
			{src && status !== 'loaded' && (
				<img
					src={src}
					alt=""
					aria-hidden="true"
					className={styles.hiddenLoader}
					onLoad={() => setStatus('loaded')}
					onError={() => setStatus('error')}
				/>
			)}

			{/* Visible image once loaded */}
			{status === 'loaded' && src && (
				<img
					src={src}
					alt={alt ?? t('image-preview-alt', 'Image preview')}
					className={styles.image}
					style={{ maxHeight }}
				/>
			)}

			{/* Placeholder: blurred during loading, static during idle/error */}
			{showPlaceholder && (
				<div className={`${styles.placeholder} ${isLoading ? styles.placeholderBlurred : ''}`}>
					<ImageIcon size={28} className={styles.placeholderIcon} />
					<span className={styles.placeholderText}>{t('image-preview-unavailable', 'No preview available')}</span>
				</div>
			)}

			{/* Spinner overlays the blurred placeholder during loading, unless an ancestor shows its own */}
			{isLoading && showSpinner && (
				<div className={styles.spinnerOverlay}>
					<span className={styles.spinner} role="status" aria-label="Loading" />
				</div>
			)}
		</div>
	);
};
